import { isEqual, mapValues } from 'es-toolkit';
import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, Grid, Typography } from '@mui/material';

import { PropertySelectField, PropertyTextInput } from '@paragon/client-tools';
import {
  EstimateInfo,
  EstimateMetricsAndMissingPrices,
  LayoutModel,
  LumberWithStockLengthAndPrice,
  ProjectEstimate,
} from '@paragon/contract-library';
import { distanceEquals } from '@paragon/geometry-library';
import { ComponentQuantityList } from '@paragon/layout-models';
import { lumbersAreEqual } from '@paragon/material-lists';
import { naturalSortByProperty } from '@paragon/utilities';

import { AuxiliaryEstimateInfoFields } from './AuxiliaryEstimateInfoFields';
import { EstimateMetricsList } from './EstimateMetricsList';
import { EstimateOtherMaterials } from './EstimateOtherMaterials';
import { Pricing } from './Pricing';

interface CreateEstimateProps {
  readonly estimateInfo: EstimateInfo;
  readonly setEstimateInfo: React.Dispatch<React.SetStateAction<EstimateInfo>>;
  readonly existingProjectEstimates: readonly ProjectEstimate[] | 'loading' | 'error';

  readonly estimateMetricsAndMissingPrices: EstimateMetricsAndMissingPrices | 'loading' | 'error';
  readonly requireNonZeroPrices: boolean;
  readonly setRequireNonZeroPrices: (require: boolean) => void;

  readonly componentDesignQuantities: { [guid: string]: number };
  readonly layoutModelsByGuid: { [guid: string]: LayoutModel };
  readonly additionalQuantityControls?: [
    string,
    React.ComponentType<{
      quantities: { [guid: string]: number };
      onQuantityChange: (componentDesignQuantities: { [guid: string]: number }) => void;
    }>,
  ][];

  readonly distanceFormatter: (inches: number) => string;

  readonly submitAttemptedWithoutAllPrices: boolean;
}

export function CreateEstimate({
  estimateInfo,
  setEstimateInfo,
  existingProjectEstimates,
  estimateMetricsAndMissingPrices,
  requireNonZeroPrices,
  setRequireNonZeroPrices,
  componentDesignQuantities,
  additionalQuantityControls,
  distanceFormatter,
  submitAttemptedWithoutAllPrices,
}: CreateEstimateProps) {
  function setQuantities(componentDesignQuantities: { [guid: string]: number }) {
    setEstimateInfo((oldEstimateInfo) => ({
      ...oldEstimateInfo,
      componentDesigns: componentDesignQuantities,
    }));
  }

  function setUserSpecifiedLumberPrice(lumberWithStockLengthAndPrice: LumberWithStockLengthAndPrice) {
    setEstimateInfo((oldEstimateInfo) => {
      const index = oldEstimateInfo.userSpecifiedLumberPrices.findIndex(
        (stateLumberWithStockLengthAndPrice) =>
          lumbersAreEqual(lumberWithStockLengthAndPrice.lumber, stateLumberWithStockLengthAndPrice.lumber) &&
          distanceEquals(
            lumberWithStockLengthAndPrice.stockLengthInInches,
            stateLumberWithStockLengthAndPrice.stockLengthInInches,
          ),
      );
      if (index === -1) throw new Error('Create Estimate Dialog: Lumber price being edited not found');
      const newUserSpecifiedLumberPrices = [...oldEstimateInfo.userSpecifiedLumberPrices];
      newUserSpecifiedLumberPrices[index] = lumberWithStockLengthAndPrice;
      return { ...oldEstimateInfo, userSpecifiedLumberPrices: newUserSpecifiedLumberPrices };
    });
  }

  const [selectedProjectEstimate, setSelectedProjectEstimate] = useState<ProjectEstimate | null>(null);
  const [differingComponentsWarning, setDifferingComponentsWarning] = useState<boolean>(false);
  function loadSelectedEstimate() {
    // Both of these cases should be handled by the disabled field on the "Load Estimate" button, but these conditionals
    // narrow the types for us
    if (selectedProjectEstimate === null) throw new Error('Project estimate not selected');
    if (typeof estimateMetricsAndMissingPrices !== 'object') throw new Error('Current pricing data not loaded');

    let estimateInfoToLoad: ProjectEstimate = {
      ...selectedProjectEstimate,
      guid: crypto.randomUUID(),
      projectSnapshotGuid: '',
      created: new Date().toISOString(),
      createdByEmail: estimateInfo.createdByEmail,
    };

    // The existing estimate may reference components that do not exist in the project any longer. If this is the case,
    // remove those entries and display a warning.
    const currentComponentDesignGuids = Object.keys(estimateInfo.componentDesigns).toSorted();
    const estimateToLoadComponentDesignGuids = Object.keys(estimateInfoToLoad.componentDesigns).toSorted();
    if (!isEqual(currentComponentDesignGuids, estimateToLoadComponentDesignGuids)) {
      estimateInfoToLoad = {
        ...estimateInfoToLoad,
        componentDesigns: mapValues(
          estimateInfo.componentDesigns,
          (_, componentDesignGuid) => estimateInfoToLoad.componentDesigns[componentDesignGuid] ?? 0,
        ),
      };
      setDifferingComponentsWarning(true);
    } else {
      setDifferingComponentsWarning(false);
    }

    // The pricing data may have been updated such that the user-specified data in the selected estimate is obsolete.
    // Filter out obsolete data by starting with the current data and applying the selected estimate data where
    // applicable.
    const userSpecifiedLumberPrices = estimateInfo.userSpecifiedLumberPrices.map(
      (currentEstimateUserSpecifiedLumberPrice) => {
        const userSpecifiedLumberPriceInSelectedEstimate = estimateInfoToLoad.userSpecifiedLumberPrices.find(
          (userSpecifiedLumberPrice) =>
            lumbersAreEqual(currentEstimateUserSpecifiedLumberPrice.lumber, userSpecifiedLumberPrice.lumber) &&
            distanceEquals(
              currentEstimateUserSpecifiedLumberPrice.stockLengthInInches,
              userSpecifiedLumberPrice.stockLengthInInches,
            ),
        );
        return userSpecifiedLumberPriceInSelectedEstimate ?? currentEstimateUserSpecifiedLumberPrice;
      },
    );
    const userSpecifiedPlatePrices = mapValues(
      estimateInfo.userSpecifiedPlatePrices,
      (currentEstimatePlatePrice, plateType) =>
        estimateInfoToLoad.userSpecifiedPlatePrices[plateType] ?? currentEstimatePlatePrice,
    );
    const userSpecifiedHangerPrices = mapValues(
      estimateInfo.userSpecifiedHangerPrices,
      (currentEstimateHangerPrice, hanger) =>
        estimateInfoToLoad.userSpecifiedHangerPrices[hanger] ?? currentEstimateHangerPrice,
    );
    // If we ever store custom hanger hardware prices among prcing data (outside of estimates), do a similar merge here
    estimateInfoToLoad = {
      ...estimateInfoToLoad,
      userSpecifiedLumberPrices,
      userSpecifiedPlatePrices,
      userSpecifiedHangerPrices,
    };

    setEstimateInfo(estimateInfoToLoad);
  }

  return (
    <Grid container spacing={3}>
      <Grid size={3}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Load an Existing Estimate
        </Typography>

        <Box sx={{ mb: 2 }}>
          {existingProjectEstimates === 'loading' ? (
            <CircularProgress />
          ) : existingProjectEstimates === 'error' ? (
            <Alert severity="error">Error loading existing project estimates</Alert>
          ) : (
            <>
              <PropertySelectField
                property="Existing Estimate"
                value={selectedProjectEstimate}
                getValue={(projectEstimate) => projectEstimate?.guid}
                getName={(projectEstimate) =>
                  projectEstimate
                    ? `${projectEstimate.name} (${Intl.DateTimeFormat('en-US', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(projectEstimate.created))} - ${projectEstimate.createdByEmail})`
                    : undefined
                }
                options={naturalSortByProperty(existingProjectEstimates, (projectEstimate) => projectEstimate.created)}
                submit={setSelectedProjectEstimate}
              />
              <Button
                fullWidth
                variant="outlined"
                onClick={loadSelectedEstimate}
                disabled={selectedProjectEstimate === null || typeof estimateMetricsAndMissingPrices !== 'object'}
              >
                Load Estimate
              </Button>
            </>
          )}
        </Box>

        <Typography variant="h5" sx={{ mb: 2 }}>
          Estimate Name
        </Typography>
        <PropertyTextInput
          property="Name"
          value={estimateInfo.name}
          submit={(name) => setEstimateInfo((oldEstimateInfo) => ({ ...oldEstimateInfo, name }))}
          fullWidth
        />

        {differingComponentsWarning && (
          <Alert severity="error">
            The loaded estimate references components which are no longer in the project. Those components are not
            included here.
          </Alert>
        )}

        <Typography variant="h5" sx={{ mb: 2 }}>
          Component Quantities
        </Typography>
        <Button
          fullWidth
          sx={{ mb: 1 }}
          variant="outlined"
          onClick={() =>
            void setEstimateInfo((oldEstimateInfo) => ({
              ...oldEstimateInfo,
              componentDesigns: mapValues(oldEstimateInfo.componentDesigns, () => 0),
            }))
          }
        >
          Clear Quantities
        </Button>
        {additionalQuantityControls?.map(([identifier, AdditionalQuantityControl]) => (
          <AdditionalQuantityControl
            quantities={componentDesignQuantities}
            onQuantityChange={setQuantities}
            key={identifier}
          />
        ))}
        <ComponentQuantityList
          quantities={estimateInfo.componentDesigns}
          setCustomQuantity={(componentDesignGuid, quantity) =>
            void setEstimateInfo((oldEstimateInfo) => ({
              ...oldEstimateInfo,
              componentDesigns: {
                ...oldEstimateInfo.componentDesigns,
                [componentDesignGuid]: quantity,
              },
            }))
          }
        />
      </Grid>

      <Grid size={3}>
        {typeof estimateMetricsAndMissingPrices === 'object' ? (
          <>
            <EstimateMetricsList
              estimateMetrics={estimateMetricsAndMissingPrices}
              estimateInfo={estimateInfo}
              distanceFormatter={distanceFormatter}
            />

            <Divider sx={{ my: 2 }} />

            <Pricing
              requireNonZeroPrices={requireNonZeroPrices}
              setRequireNonZeroPrices={setRequireNonZeroPrices}
              userSpecifiedLumberPrices={estimateInfo.userSpecifiedLumberPrices}
              userSpecifiedPlatePrices={estimateInfo.userSpecifiedPlatePrices}
              userSpecifiedHangerPrices={estimateInfo.userSpecifiedHangerPrices}
              userSpecifiedCustomHangerPrices={estimateInfo.userSpecifiedCustomHangerPrices}
              setUserSpecifiedLumberPrice={setUserSpecifiedLumberPrice}
              setUserSpecifiedPlatePrice={(plateType, price) =>
                setEstimateInfo((oldEstimateInfo) => ({
                  ...oldEstimateInfo,
                  userSpecifiedPlatePrices: { ...oldEstimateInfo.userSpecifiedPlatePrices, [plateType]: price },
                }))
              }
              setUserSpecifiedHangerPrice={(hanger, price) =>
                setEstimateInfo((oldEstimateInfo) => ({
                  ...oldEstimateInfo,
                  userSpecifiedHangerPrices: { ...oldEstimateInfo.userSpecifiedHangerPrices, [hanger]: price },
                }))
              }
              setUserSpecifiedCustomHangerPrice={(hanger, price) =>
                setEstimateInfo((oldEstimateInfo) => ({
                  ...oldEstimateInfo,
                  userSpecifiedCustomHangerPrices: {
                    ...oldEstimateInfo.userSpecifiedCustomHangerPrices,
                    [hanger]: price,
                  },
                }))
              }
              includeHangerPricesInEstimate={estimateInfo.includeHangerPricing}
              setIncludeHangerPricesInEstimate={(includeHangerPricing) =>
                setEstimateInfo((oldEstimateInfo) => ({ ...oldEstimateInfo, includeHangerPricing }))
              }
              distanceFormatter={distanceFormatter}
              submitAttemptedWithoutAllPrices={submitAttemptedWithoutAllPrices}
            />
          </>
        ) : estimateMetricsAndMissingPrices === 'loading' ? (
          <CircularProgress />
        ) : (
          <Typography>Error loading estimate metrics and missing prices.</Typography>
        )}
      </Grid>

      <Grid size={3}>
        <EstimateOtherMaterials estimateInfo={estimateInfo} setEstimateInfo={setEstimateInfo} />
      </Grid>

      <Grid size={3}>
        <AuxiliaryEstimateInfoFields estimateInfo={estimateInfo} setEstimateInfo={setEstimateInfo} />
      </Grid>
    </Grid>
  );
}
